// src/pages/SearchPage.tsx

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import PersonIcon from "@mui/icons-material/Person";
import AlbumIcon from "@mui/icons-material/Album";
import ExploreIcon from "@mui/icons-material/Explore";
import RefreshIcon from "@mui/icons-material/Refresh";
import LockIcon from "@mui/icons-material/Lock";
import TuneIcon from "@mui/icons-material/Tune";
import QueueMusicIcon from "@mui/icons-material/QueueMusic";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { useSearchParams, Link, useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "@/config";
import { useAudio } from "@/context/AudioProvider";
import { useAuth } from "@/context/AuthProvider";
import { getAuthHeaders } from "@/services/authService";
import AddToMixtapeDialog from "@/components/mixtapes/AddToMixtapeDialog";

const SearchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const location = useLocation();
  const { state: audioState, setUIPriority } = useAudio();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  // Keep track of previous query to detect changes
  const prevQueryRef = useRef(query);

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState({
    artists: [],
    albums: [],
    tracks: [],
  });
  const [mixtapeDialog, setMixtapeDialog] = useState<{ open: boolean; trackId?: string }>({ open: false });
  
  // SoundCheck dialog state
  const [soundCheckDialogOpen, setSoundCheckDialogOpen] = useState(false);
  const [selectedRestrictedTrack, setSelectedRestrictedTrack] = useState<any>(null);
  
  // Track mount status to prevent state updates after unmount
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Clear results when query changes dramatically
  useEffect(() => {
    // If query changed significantly (not just refinement), reset results
    if (prevQueryRef.current && 
        query && 
        !query.startsWith(prevQueryRef.current) && 
        !prevQueryRef.current.startsWith(query)) {
      setResults({ artists: [], albums: [], tracks: [] });
    }
    
    prevQueryRef.current = query;
  }, [query]);

  // Helper function to check if a track is restricted
  const isTrackRestricted = (track: any) => {
    return track.access_type === 'soundcheck' && track.access_granted !== true;
  };

  const handleQueueTrack = (track: any, e: React.MouseEvent) => {
    e.stopPropagation();
    console.log("Queue track from search", track.title);
  };

  const handleAddTrackToMixtape = (track: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setMixtapeDialog({ open: true, trackId: String(track.id) });
  };

  // Handle restricted track click
  const handleRestrictedTrackClick = (track: any) => {
    setSelectedRestrictedTrack(track);
    setSoundCheckDialogOpen(true);
  };

  // Handle SoundCheck dialog close
  const handleSoundCheckDialogClose = () => {
    setSoundCheckDialogOpen(false);
    setSelectedRestrictedTrack(null);
  };

  // Handle view album from dialog
  const handleViewAlbum = () => {
    if (selectedRestrictedTrack) {
      const artistPath = selectedRestrictedTrack.artist_slug || selectedRestrictedTrack.artist_id;
      const albumPath = selectedRestrictedTrack.album_slug || selectedRestrictedTrack.album_id;
      
      if (artistPath && albumPath) {
        // Use hierarchical URL structure
        navigate(`/artist/${artistPath}/album/${albumPath}`);
      } else if (albumPath) {
        // Fallback to legacy album URL
        navigate(`/album/${albumPath}`);
      }
    }
    handleSoundCheckDialogClose();
  };

  const handleSearch = useCallback(
    async (q: string, opts?: { cancelSignal?: AbortSignal }) => {
    if (!q.trim() || !isMountedRef.current) return;

    console.log(`[Search] Starting search for "${q}" - ${new Date().toISOString()}`);
    console.log(`[Search] Audio playing: ${audioState.isPlaying}`);

    // Tell audio player to prioritize UI during search
    if (audioState.isPlaying) {
      setUIPriority(true);
    }

    setLoading(true);
    setError(null);

    const ctrl = new AbortController();
    let timedOut = false;
    const onCancel = () => ctrl.abort();
    opts?.cancelSignal?.addEventListener("abort", onCancel);
    const timeoutId = setTimeout(() => {
      timedOut = true;
      ctrl.abort();
    }, 28000);

    try {
      console.log(`[Search] Fetching data from API`);
      const startTime = performance.now();
      const authHeaders = getAuthHeaders();
      const searchRes = await fetch(
        `${API_BASE_URL}/public/search?q=${encodeURIComponent(q)}`,
        { signal: ctrl.signal, headers: authHeaders }
      );

      const fetchDuration = performance.now() - startTime;
      console.log(`[Search] API fetch completed in ${fetchDuration.toFixed(2)}ms`);

      clearTimeout(timeoutId);
      opts?.cancelSignal?.removeEventListener("abort", onCancel);

      if (!searchRes.ok) {
        throw new Error("Search request failed");
      }

      if (!isMountedRef.current) {
        console.log(`[Search] Component unmounted during API call, aborting`);
        return;
      }

      const parseStart = performance.now();
      const data = await searchRes.json();
      const parseDuration = performance.now() - parseStart;

      const artistData = { artists: data.artists || [] };
      const albumData = { albums: data.albums || [] };
      const trackData = { tracks: data.tracks || [] };

      console.log(`[Search] JSON parsing completed in ${parseDuration.toFixed(2)}ms`);
      console.log(`[Search] Results: ${artistData.artists.length} artists, ${albumData.albums.length} albums, ${trackData.tracks.length} tracks`);
      if (data.massive_results) {
        console.log(`[Search] MassiveMusic: ${data.massive_results.artists} artists, ${data.massive_results.albums} albums, ${data.massive_results.tracks} tracks`);
      }

      // Create a lookup map for artist slugs by ID
      const artistSlugsMap = new Map();
      if (artistData.artists && artistData.artists.length > 0) {
        artistData.artists.forEach((artist: any) => {
          if (artist.id && artist.slug) {
            artistSlugsMap.set(artist.id, artist.slug);
          }
        });
      }

      // Ensure albums have artist_slug
      const processedAlbums = (albumData.albums || []).map((album: any) => {
        // First check if the API already returned artist_slug
        if (!album.artist_slug && album.artist_id) {
          // If not, try to find it in our lookup map from the artists search results
          const artistSlug = artistSlugsMap.get(album.artist_id);
          if (artistSlug) {
            album.artist_slug = artistSlug;
          } else if (album.artists?.slug) {
            // Fallback to nested artist object if available
            album.artist_slug = album.artists.slug;
          }
        }
        return album;
      });

      // Ensure tracks have artist_slug and access control fields
      const processedTracks = (trackData.tracks || []).map((track: any) => {
        // First check if the API already returned artist_slug
        if (!track.artist_slug && track.artist_id) {
          // If not, try to find it in our lookup map
          const artistSlug = artistSlugsMap.get(track.artist_id);
          if (artistSlug) {
            track.artist_slug = artistSlug;
          } else if (track.artists?.slug) {
            // Fallback to nested artist object if available
            track.artist_slug = track.artists.slug;
          }
        }
        return track;
      });

      // 🔍 DEBUG: Log access control info for first few tracks
      console.log(`[Search] Access control debug for first 3 tracks:`);
      processedTracks.slice(0, 3).forEach((track: any) => {
        console.log(`  - "${track.title}": access_type="${track.access_type}", access_granted=${track.access_granted}`);
      });

      // Guard against component unmounting
      if (!isMountedRef.current) {
        console.log(`[Search] Component unmounted during processing, aborting`);
        return;
      }

      console.log(`[Search] Setting results in state`);
      setResults({
        artists: artistData.artists || [],
        albums: processedAlbums,
        tracks: processedTracks,
      });

      // Set appropriate tab based on results
      if (artistData.artists?.length > 0) setTabValue(0);
      else if (processedAlbums.length > 0) setTabValue(1);
      else if (processedTracks.length > 0) setTabValue(2);
      else setTabValue(0);
      
      console.log(`[Search] Search completed successfully`);
    } catch (err) {
      clearTimeout(timeoutId);
      opts?.cancelSignal?.removeEventListener("abort", onCancel);
      console.error(`[Search] Error during search:`, err);

      if (isMountedRef.current) {
        const aborted =
          err instanceof DOMException && err.name === "AbortError";
        if (aborted) {
          if (timedOut) {
            setError("Search timed out. Please try again.");
          }
          // Superseded by newer query (cancelSignal) — no error toast
        } else {
          setError("Failed to load search results. Please try again.");
        }
      }
    } finally {
      clearTimeout(timeoutId);
      opts?.cancelSignal?.removeEventListener("abort", onCancel);
      if (isMountedRef.current) {
        setLoading(false);
        if (audioState.isPlaying) {
          console.log(`[Search] Releasing UI priority`);
          setUIPriority(false);
        }
      }
    }
  }, [audioState.isPlaying, setUIPriority]);

  // Debounced search: one in-flight request per query; abort when query changes
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length <= 1) {
      if (trimmed.length === 0) {
        setResults({ artists: [], albums: [], tracks: [] });
      }
      return;
    }

    const cancel = new AbortController();
    const debounceMs = audioState.isPlaying ? 200 : 420;
    if (audioState.isPlaying) {
      setUIPriority(true);
    }

    const debounceId = window.setTimeout(() => {
      handleSearch(trimmed, { cancelSignal: cancel.signal });
    }, debounceMs);

    return () => {
      window.clearTimeout(debounceId);
      cancel.abort();
    };
  }, [query, handleSearch, audioState.isPlaying, setUIPriority]);

  const handleTabChange = (_: any, newValue: number) => setTabValue(newValue);
  
  const handleRefresh = () => {
    if (query.trim().length > 1) {
      setError(null);
      // Gently schedule the search to avoid audio interruption
      if (audioState.isPlaying) {
        setUIPriority(true);
        setTimeout(() => {
          requestAnimationFrame(() => {
            handleSearch(query.trim());
          });
        }, 50);
      } else {
        handleSearch(query.trim());
      }
    }
  };

  // Return the search page (removed forced remounting)
  return (
    <>
      <Box sx={{ pt: 10, pb: 4, maxWidth: "1200px", mx: "auto", px: 2 }}>
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }} 
          action={
            <RefreshIcon 
              cursor="pointer" 
              onClick={handleRefresh}
            />
          }
        >
          {error}
        </Alert>
      )}
      
      {query ? (
        <>
          <Tabs value={tabValue} onChange={handleTabChange} centered>
            <Tab label={<Box display="flex" alignItems="center"><PersonIcon sx={{ mr: 1 }} />Artists ({results.artists.length})</Box>} />
            <Tab label={<Box display="flex" alignItems="center"><AlbumIcon sx={{ mr: 1 }} />Albums ({results.albums.length})</Box>} />
            <Tab label={<Box display="flex" alignItems="center"><MusicNoteIcon sx={{ mr: 1 }} />Tracks ({results.tracks.length})</Box>} />
            <Tab 
              component={Link} 
              to="/discover" 
              label={<Box display="flex" alignItems="center"><ExploreIcon sx={{ mr: 1 }} />Discover</Box>} 
            />
          </Tabs>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* ARTISTS */}
              <Box hidden={tabValue !== 0} sx={{ py: 3 }}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Image</TableCell>
                        <TableCell>Artist Name</TableCell>
                        <TableCell>Genre</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.artists.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography>No artists found matching "{query}"</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        results.artists.map((artist: any) => {
                          const artistPath = artist.slug || artist.id;
                          
                          return (
                            <TableRow
                              key={artist.id}
                              hover
                              onClick={() => navigate(`/artist/${artistPath}`)}
                              sx={{ 
                                textDecoration: "none", 
                                cursor: "pointer",
                                '&:hover': {
                                  backgroundColor: 'action.hover'
                                }
                              }}
                            >
                              <TableCell>
                                <Avatar src={artist.image_url || undefined} alt={artist.name} />
                              </TableCell>
                              <TableCell>{artist.name}</TableCell>
                              <TableCell>
                                {(artist.genres || []).map((genre: string, index: number) => (
                                  <Chip key={index} label={genre} size="small" sx={{ mr: 0.5 }} />
                                ))}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* ALBUMS */}
              <Box hidden={tabValue !== 1} sx={{ py: 3 }}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Cover</TableCell>
                        <TableCell>Album Title</TableCell>
                        <TableCell>Artist</TableCell>
                        <TableCell>Year</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.albums.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography>No albums found matching "{query}"</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        results.albums.map((album: any) => {
                          const artistPath = album.artist?.slug || album.artist?.id || album.artist_id;
                          const albumPath = album.slug || album.id;
                          
                          return (
                            <TableRow
                              key={album.id}
                              hover
                              onClick={() => navigate(`/artist/${artistPath}/album/${albumPath}`)}
                              sx={{ 
                                textDecoration: "none", 
                                cursor: "pointer",
                                '&:hover': {
                                  backgroundColor: 'action.hover'
                                }
                              }}
                            >
                              <TableCell>
                                <Avatar src={album.artwork_url || undefined} alt={album.title} variant="rounded" />
                              </TableCell>
                              <TableCell>{album.title}</TableCell>
                              <TableCell>{album.artists?.name || album.artist_name || "Unknown Artist"}</TableCell>
                              <TableCell>{album.release_date ? new Date(album.release_date).getFullYear() : ""}</TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* TRACKS */}
              <Box hidden={tabValue !== 2} sx={{ py: 3 }}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Cover</TableCell>
                        <TableCell>Track Title</TableCell>
                        <TableCell>Artist</TableCell>
                        <TableCell>Album</TableCell>
                        <TableCell>Access</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.tracks.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography>No tracks found matching "{query}"</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        results.tracks.map((track: any) => {
                          const artistPath = track.artist_slug || track.artist_id;
                          const restricted = isTrackRestricted(track);
                          
                          return (
                            <TableRow
                              key={track.id}
                              hover
                              onClick={() => {
                                if (restricted) {
                                  handleRestrictedTrackClick(track);
                                } else {
                                  // Navigate to the album page that contains this track
                                  const artistPath = track.artist_slug || track.artist_id;
                                  const albumPath = track.album_slug || track.album_id;
                                  navigate(`/artist/${artistPath}/album/${albumPath}`);
                                }
                              }}
                              sx={{ 
                                textDecoration: "none", 
                                cursor: "pointer",
                                opacity: restricted ? 0.7 : 1,
                                '&:hover': {
                                  backgroundColor: 'action.hover'
                                }
                              }}
                            >
                              <TableCell>
                                <Box sx={{ position: 'relative' }}>
                                  <Avatar src={track.artwork_url || undefined} alt={track.title} variant="rounded" />
                                  {restricted && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        bottom: 0,
                                        left: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                        borderRadius: 1,
                                      }}
                                    >
                                      <LockIcon sx={{ color: 'grey.400', fontSize: 16 }} />
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: restricted ? 'grey.500' : 'inherit',
                                    fontWeight: restricted ? 'normal' : 'inherit'
                                  }}
                                >
                                  {track.title}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography 
                                  variant="body2" 
                                  sx={{ color: restricted ? 'grey.600' : 'inherit' }}
                                >
                                  {track.artist_name || "Unknown Artist"}
                                </Typography>
                              </TableCell>
                            <TableCell>
                                <Typography 
                                  variant="body2" 
                                  sx={{ color: restricted ? 'grey.600' : 'inherit' }}
                                >
                                  {track.album_title || ""}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {restricted ? (
                                  <Chip 
                                    icon={<LockIcon />} 
                                    label="SoundCheck" 
                                    size="small" 
                                    sx={{ 
                                      backgroundColor: 'grey.800',
                                      color: 'grey.400',
                                      '& .MuiChip-icon': {
                                        color: 'grey.400'
                                      }
                                    }} 
                                  />
                                ) : track.access_type === 'soundcheck' ? (
                                  <Chip 
                                    icon={<LockIcon sx={{ opacity: 0.5 }} />} 
                                    label="SoundCheck (Owned)" 
                                    size="small" 
                                    sx={{ 
                                      backgroundColor: 'success.dark',
                                      color: 'success.light',
                                      '& .MuiChip-icon': {
                                        color: 'success.light'
                                      }
                                    }} 
                                  />
                                ) : track.access_type === 'standard' ? (
                                  <Chip 
                                    label="Standard" 
                                    size="small" 
                                    color="success"
                                    variant="outlined"
                                  />
                                ) : null}
                              </TableCell>
                              <TableCell align="right">
                                {restricted ? (
                                  <LockIcon fontSize="small" sx={{ color: 'warning.main' }} />
                                ) : (
                                  <>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleQueueTrack(track, e)}
                                    >
                                      <QueueMusicIcon fontSize="inherit" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleAddTrackToMixtape(track, e)}
                                    >
                                      <PlaylistAddIcon fontSize="inherit" />
                                    </IconButton>
                                  </>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}

          {/* SoundCheck Access Dialog */}
          <Dialog
            open={soundCheckDialogOpen}
            onClose={handleSoundCheckDialogClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                backgroundColor: 'background.paper',
                borderRadius: 2,
              }
            }}
          >
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Box
                  sx={{
                    p: 1,
                    backgroundColor: 'primary.main',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <TuneIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" component="div">
                  SoundCheck Exclusive
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This track is part of an exclusive SoundCheck album.
              </Typography>
              {selectedRestrictedTrack && (
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  "{selectedRestrictedTrack.title}" by {selectedRestrictedTrack.artist_name}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                Unlock this track and support the artist by purchasing access.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleSoundCheckDialogClose} color="inherit">
                Maybe Later
              </Button>
              <Button 
                onClick={handleViewAlbum} 
                variant="contained" 
                color="primary"
              >
                View Album
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <Typography variant="h6" color="text.secondary">
            Enter a search term to find artists, albums, and tracks
          </Typography>
        </Box>
      )}
      </Box>
      <AddToMixtapeDialog
        open={mixtapeDialog.open}
        trackId={mixtapeDialog.trackId}
        onClose={() => setMixtapeDialog({ open: false })}
      />
    </>
  );
};

export default SearchPage;
